import type { AdminSimulationDataFrameRow as SimulationDataFrameRow } from "@/types_api/backend/transport/admin_list_simulation_data_frames_response"

export interface SimulationDataFrameMark {
  id: string
  tableName: string
  startMs: number
  endMs: number
  row: SimulationDataFrameRow
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function stringValue(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return trimmed || null
}

function toSimulationDataFrameMark(
  tableName: string,
  row: SimulationDataFrameRow,
  index: number,
): SimulationDataFrameMark | null {
  const startMs = finiteNumber(row.frame.time_offset_milliseconds)
  if (startMs == null) {
    return null
  }
  const endMs = Math.max(
    startMs,
    finiteNumber(row.frame.end_offset_milliseconds) ?? startMs,
  )
  const frameId = stringValue(row.frame.frame_id)

  return {
    id: `${tableName}:${frameId ?? `${startMs}:${index}`}`,
    tableName,
    startMs,
    endMs,
    row,
  }
}

export function buildSimulationDataFrameMarks(
  rowsByTable: Record<string, SimulationDataFrameRow[]>,
): SimulationDataFrameMark[] {
  return Object.entries(rowsByTable)
    .flatMap(([tableName, rows]) =>
      rows.flatMap((row, index) => {
        const mark = toSimulationDataFrameMark(tableName, row, index)
        return mark ? [mark] : []
      }),
    )
    .sort(
      (left, right) =>
        left.startMs - right.startMs ||
        left.tableName.localeCompare(right.tableName) ||
        left.id.localeCompare(right.id),
    )
}

export function formatSimulationOffset(milliseconds: number): string {
  if (milliseconds < 1000) {
    return `${Math.round(milliseconds)}ms`
  }
  const seconds = milliseconds / 1000
  if (seconds < 60) {
    return `${seconds.toFixed(seconds < 10 ? 2 : 1)}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
}

export function simulationDataFrameSpeaker(
  mark: SimulationDataFrameMark,
): "agent" | "persona" | null {
  const role = mark.row.frame.role
  if (role === "agent" || role === "assistant") {
    return "agent"
  }
  if (role === "persona" || role === "user" || role === "customer") {
    return "persona"
  }
  return null
}
